import { Badge, Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger, Tab, TabList, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowRight16Regular, ArrowSwap16Regular, ArrowTrending16Regular, CalendarClock16Regular, GlanceHorizontal16Regular, LockClosed16Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/app-dialog";
import { FuiButton, FuiDropdown, type FuiOption } from "@/components/fui-control";
import { ListSearch, ListToolbar } from "@/components/list-toolbar";
import { contentShell } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { ReportChangesView, type CategoryFilter } from "@/features/reports/ReportChangesView";
import { ReportForecastView } from "@/features/reports/ReportForecastView";
import { ReportStatusView } from "@/features/reports/ReportStatusView";
import { StatusHelp } from "@/features/reports/StatusHelp";
import { ReportTrendView } from "@/features/reports/ReportTrendView";
import { CHANGE_CATEGORIES, CHANGE_CATEGORY_LABEL, REPORT_PERIOD_LIMIT, type ChangeCategory } from "@/features/reports/report-model";
import { CURRENT_KEY, useMaskedMoney, useReportPoints, useReportStyles, type ReportPoint } from "@/features/reports/report-ui";
import type { RecordKind } from "@/engine/types";
import { useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { RecordAddMenu } from "@/features/records/RecordAddMenu";
import { RECORD_KINDS } from "@/store/record-ops";
import { useProjectStore } from "@/store/project-store";

type View = "status" | "changes" | "trend" | "forecast";
const VIEWS: View[] = ["status", "changes", "trend", "forecast"];
const isDev = process.env.NODE_ENV !== "production";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  nowrap: { whiteSpace: "nowrap", flexShrink: 0 },
  pointPicker: { width: "220px", flexShrink: 0 },
  /** 「12월 4주차」가 잘리지 않는 너비. 이전·이후 접두어 없이 주차만 보여 준다. */
  weekPicker: {
    width: "108px",
    flexShrink: 0,
    "& .fui-Dropdown__button": {
      overflow: "hidden",
    },
  },
  arrow: { color: tokens.colorNeutralForeground3, display: "inline-flex", flexShrink: 0 },
});

/** 「10월 2주차 주간 보고」→「10월 2주차」. 현재 시점은 「현재」. */
function pointSelectLabel(p: ReportPoint): string {
  if (p.kind === "current") return "현재";
  const week = /^(\d{1,2}월)\s*(\d+)주차/.exec(p.label);
  return week ? `${week[1]} ${week[2]}주차` : p.label;
}

function pointOptions(points: ReportPoint[], compact = false): FuiOption[] {
  return points.map((p) => ({ value: p.key, label: compact ? pointSelectLabel(p) : p.label, description: p.sub }));
}

export function ProjectReportsPage() {
  const styles = useStyles();
  const rs = useReportStyles();
  const [params, setParams] = useSearchParams();
  const points = useReportPoints();
  const masked = useMaskedMoney();
  const hasDemo = useProjectStore((s) => s.reports.some((r) => r.source === "demo"));
  const [confirmClearDemo, setConfirmClearDemo] = useState(false);
  const surface = usePlanSurface();
  const own = useOwnWork();
  const canEditSchedule = surface.editSchedule;
  const recordKinds = own.restricted ? RECORD_KINDS.filter((k) => k === "issue" || k === "risk" || k === "change") : RECORD_KINDS;

  const rawView = params.get("view");
  const view: View = VIEWS.includes(rawView as View) ? (rawView as View) : "status";
  const atKey = points.byKey.has(params.get("at") ?? "") ? params.get("at")! : CURRENT_KEY;
  const defaultFrom = points.periods[0]?.key ?? points.checkpoints[0]?.key ?? "";
  const fromKey = points.byKey.has(params.get("from") ?? "") ? params.get("from")! : defaultFrom;
  const toKey = points.byKey.has(params.get("to") ?? "") ? params.get("to")! : CURRENT_KEY;
  const rawCat = params.get("cat");
  const category: CategoryFilter = rawCat && (CHANGE_CATEGORIES as string[]).includes(rawCat) ? (rawCat as ChangeCategory) : "all";
  const query = params.get("q") ?? "";

  const patch = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === "") p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };
  const openChanges = (from?: string, to?: string, cat?: ChangeCategory) => patch({ view: "changes", from: from ?? null, to: to ?? null, cat: cat ?? null, q: null });
  const openStatus = (key: string) => patch({ view: null, at: key === CURRENT_KEY ? null : key, q: null });
  const selectView = (next: View) => {
    if (next === view) return;
    patch({ view: next === "status" ? null : next, q: null });
  };
  const hasChangeRange = points.byKey.has(fromKey);
  const closePeriod = () => useProjectStore.getState().openDialog("report-close");
  const createDemo = () => useProjectStore.getState().createDemoReports();
  const addRecord = (kind: RecordKind) => useProjectStore.getState().addRecord(kind);
  usePageAction(null);

  const statusPrev = points.previousOf(atKey);

  return (
    <div className={styles.page}>
      <ListToolbar
        title="현황"
        titleAside={<StatusHelp />}
        headingAction={surface.editIssue ? <RecordAddMenu kinds={recordKinds} onAdd={addRecord} sample={isDev} /> : undefined}
        sections={
          <TabList
            size="small"
            selectedValue={view}
            aria-label="현황"
            onTabSelect={(_, data) => {
              const next = data.value;
              if (next === "status" || next === "changes" || next === "trend" || next === "forecast") selectView(next);
            }}
          >
            <Tab value="status" icon={<GlanceHorizontal16Regular />}>현황</Tab>
            <Tab value="changes" icon={<ArrowSwap16Regular />}>변경</Tab>
            <Tab value="trend" icon={<ArrowTrending16Regular />}>추이</Tab>
            <Tab value="forecast" icon={<CalendarClock16Regular />}>예측</Tab>
          </TabList>
        }
        search={
          view === "changes" && hasChangeRange ? (
            <ListSearch value={query} onChange={(value) => patch({ q: value || null })} />
          ) : undefined
        }
        filters={
          view === "status" ? (
            <FuiDropdown className={styles.pointPicker} value={atKey} options={pointOptions([points.current, ...points.periods])} onValueChange={(v) => openStatus(v)} aria-label="보고 대상" />
          ) : view === "changes" ? (
            <>
              {hasChangeRange ? (
                <SegmentedControl
                  ariaLabel="변경 구분"
                  value={category}
                  onChange={(v) => patch({ cat: v === "all" ? null : v })}
                  options={[{ value: "all", label: "전체" }, ...CHANGE_CATEGORIES.map((c) => ({ value: c, label: CHANGE_CATEGORY_LABEL[c] }))]}
                />
              ) : null}
              <FuiDropdown
                className={styles.weekPicker}
                placeholder="비교할 시점"
                value={fromKey}
                options={pointOptions([...points.periods, ...points.checkpoints], true)}
                onValueChange={(v) => patch({ from: v })}
                aria-label="이전 시점"
                title={points.byKey.get(fromKey)?.label}
                disabled={!points.periods.length && !points.checkpoints.length}
              />
              <span className={styles.arrow} aria-hidden>
                <ArrowRight16Regular />
              </span>
              <FuiDropdown
                className={styles.weekPicker}
                value={toKey}
                options={pointOptions([points.current, ...points.periods], true)}
                onValueChange={(v) => patch({ to: v === CURRENT_KEY ? null : v })}
                aria-label="이후 시점"
                title={points.byKey.get(toKey)?.label}
              />
            </>
          ) : undefined
        }
        trail={
          view === "status" ? (
            <span className={rs.meta}>{statusPrev ? `비교: ${statusPrev.label}` : "직전 마감 없음 — 누적 기준으로 보고"}</span>
          ) : view === "trend" ? (
            <span className={rs.meta}>마감 {points.periods.length}개 · 현재 포함 {points.periods.length + 1}개 시점</span>
          ) : undefined
        }
        tools={
          <>
            {masked && (
              <Badge appearance="tint" color="warning" icon={<LockClosed16Regular />}>
                금액 마스킹
              </Badge>
            )}
            {canEditSchedule && (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <FuiButton className={styles.nowrap} appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label="현황 더 보기" />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={closePeriod}>보고 기간 마감</MenuItem>
                <MenuDivider />
                <MenuItem onClick={createDemo}>{hasDemo ? "예시 기간 다시 만들기" : "예시 기간 만들기"}</MenuItem>
                <MenuItem disabled={!hasDemo} onClick={() => setConfirmClearDemo(true)}>
                  예시 기간 지우기…
                </MenuItem>
                <MenuDivider />
                <MenuItem disabled>마감은 최대 {REPORT_PERIOD_LIMIT}개까지 보관돼요</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
            )}
          </>
        }
      />

      <div className={rs.body}>
        {view === "status" && (
          <ReportStatusView
            atKey={atKey}
            onOpenChanges={(cat) => openChanges(statusPrev?.key, atKey === CURRENT_KEY ? undefined : atKey, cat)}
            onClosePeriod={canEditSchedule ? closePeriod : undefined}
            onCreateDemo={canEditSchedule ? createDemo : undefined}
          />
        )}
        {view === "changes" && (
          <ReportChangesView
            fromKey={fromKey}
            toKey={toKey}
            category={category}
            query={query}
            canPromote={canEditSchedule}
            onPromoted={(key) => patch({ from: key })}
          />
        )}
        {view === "trend" && <ReportTrendView canManage={canEditSchedule} onOpenStatus={openStatus} onOpenChanges={(from, to) => openChanges(from, to === CURRENT_KEY ? undefined : to)} />}
        {view === "forecast" && <ReportForecastView />}
      </div>

      <ConfirmDialog
        open={confirmClearDemo}
        title="예시 기간 지우기"
        message={<>예시로 만든 보고 기간을 모두 지울까요? 직접 마감한 기간과 기준선은 그대로 남습니다.</>}
        confirmLabel="지우기"
        onCancel={() => setConfirmClearDemo(false)}
        onConfirm={() => {
          useProjectStore.getState().clearDemoReports();
          setConfirmClearDemo(false);
        }}
      />
    </div>
  );
}
